"use client";

/**
 * /o — Active-org client resolver.
 *
 * The middleware catches flat legacy paths (/dashboard, /envelopes, /settings)
 * and issues a 307 to /o?next=<encoded-path>. This page reads the active org
 * from auth context (no cookie) and redirects to /o/<slug><next>.
 *
 * MUST wrap useSearchParams() in <Suspense> per Next.js App Router requirements
 * (same pattern as the Turnstile integration — see project MEMORY).
 *
 * Error handling note: the showError condition is DERIVED at render time (not
 * latched in state) so that a failed Retry correctly re-evaluates after
 * refreshOrgs() settles. The `retrying` flag shows a spinner during the
 * in-flight retry call and suppresses the error branch, preventing a wedge.
 *
 * When orgsLoaded is true, activeOrg is still null, and retrying is false, the
 * most likely cause is a fetch error (orgsError set) or a genuinely org-less
 * account. If the user's home org IS set and the fetch fails, resolveActiveOrg
 * returns that org → activeOrg is truthy → the resolver redirects immediately
 * and any remaining error surfaces at the [org] guard instead.
 */

import { Suspense, useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import FullScreenSpinner from "@/components/ui/FullScreenSpinner";
import FullScreenError from "@/components/ui/FullScreenError";
import OrphanScreen from "@/components/ui/OrphanScreen";

// ── Inner resolver — reads searchParams (requires Suspense boundary) ──────────

function OResolver() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { isLoading, isAuthenticated, activeOrg, orgsLoaded, orgsError, refreshOrgs, logout, getAccessToken } = useAuth();

  // Shows a spinner DURING an in-flight retry so the error branch doesn't
  // immediately re-render with wrong copy while the fetch is in-flight.
  const [retrying, setRetrying] = useState(false);

  // Preserve the originally-requested path (e.g. /envelopes/abc, /dashboard/billing).
  // Only accept a leading-slash path — a bare word like "dashboard" (no slash) would
  // build a garbled URL. Fall back to /dashboard for anything that doesn't qualify.
  const raw = searchParams.get("next");
  const next = raw && raw.startsWith("/") ? raw : "/dashboard";

  // A path that names a RESOURCE resolves from the resource; a path that names a
  // VIEW resolves from the viewer. Prepending the viewer's active workspace to
  // /envelopes/<id> was the second, applied to the first — so a sender in two
  // workspaces following a link from a notification email (or back from signing)
  // landed in whichever workspace they happened to be in, and was told the
  // envelope did not exist. Ask the envelope which workspace owns it.
  const resourceId = next.match(/^\/envelopes\/([0-9a-fA-F-]{36})(?:\/|$)/)?.[1] ?? null;
  const [foreignOrgDenied, setForeignOrgDenied] = useState(false);

  useEffect(() => {
    if (isLoading) return; // auth still resolving — wait

    if (!isAuthenticated) {
      router.replace("/login");
      return;
    }

    if (!activeOrg) return; // orphan / still loading — handled below

    if (!resourceId) {
      router.replace(`/o/${activeOrg.slug}${next}`);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { org_slug } = await apiFetch<{ org_slug: string }>(
          `/envelopes/${resourceId}/workspace`,
          { token: getAccessToken() ?? undefined },
        );
        if (!cancelled) router.replace(`/o/${org_slug}${next}`);
      } catch (e) {
        if (cancelled) return;
        // 404 means "not in any workspace of yours" — say so, rather than
        // dropping the viewer into the wrong workspace to be told the envelope
        // does not exist. Anything else (network, 5xx) degrades to the previous
        // behaviour: a failed lookup must never be a dead end.
        const notFound = e instanceof Error && /404|not found/i.test(e.message);
        if (notFound) setForeignOrgDenied(true);
        else router.replace(`/o/${activeOrg.slug}${next}`);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoading, isAuthenticated, activeOrg, next, resourceId, router, getAccessToken]);

  // Derive the error condition at render — NOT latched in state — so that after
  // a failed Retry the condition re-evaluates as soon as orgsLoaded/activeOrg
  // update, with no wedge into a permanent spinner.
  const showError = !isLoading && isAuthenticated && orgsLoaded && !activeOrg && !retrying;

  const handleRetry = () => {
    setRetrying(true);
    Promise.resolve(refreshOrgs()).finally(() => setRetrying(false));
  };

  // The envelope exists for someone, but not for this account — or not at all.
  // The server answers both with 404 on purpose, so we must not claim to know
  // which. Said plainly here rather than by dumping the viewer into their own
  // workspace and letting the envelope page report "not found" about the wrong
  // workspace.
  if (foreignOrgDenied) {
    return (
      <FullScreenError
        onSignOut={logout}
        message="That envelope isn't in any of your workspaces. If someone shared it with you, ask them to add you to the workspace that owns it."
      />
    );
  }

  if (showError) {
    if (orgsError) {
      // Network / API error — a retry is meaningful
      return (
        <FullScreenError
          onRetry={handleRetry}
          onSignOut={logout}
          message="We couldn't load your workspaces. Check your connection and try again."
        />
      );
    }
    // Empty memberships with no fetch error — the user may be orphaned.
    // OrphanScreen probes workspace-status then either shows the orphan UI
    // (explicit opt-in personal-org CTA) or falls back to a generic retry.
    return (
      <OrphanScreen
        onSignOut={logout}
        fallback={
          <FullScreenError
            onRetry={handleRetry}
            onSignOut={logout}
            message="We couldn't load your workspaces. Check your connection and try again."
          />
        }
      />
    );
  }

  // Normal transit — spinner while auth resolves, memberships fetch is in-flight,
  // or a retry is in progress.
  return <FullScreenSpinner />;
}

// ── Public default export — Suspense wrapper required by Next.js App Router ──

export default function OPage() {
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <OResolver />
    </Suspense>
  );
}
